import { GeoLocation, Location, Zmanim, HDate, HebrewCalendar, flags, MoladEvent, Event } from '@hebcal/core';
import { SunCalc } from 'suncalc';
import { getOmerDay, getSefirahInfo } from './sefirasHaomer';
import { getSpecialDayForDate, SpecialDayTypeEnum } from './calendar';

const latitude = 41.189120;
const longitude = -74.055079;
const elevation = 167.67;
const tzid = 'America/New_York';
const gloc = new GeoLocation('130 camp hill', latitude, longitude, elevation, tzid);
const location = new Location(latitude, longitude, false, tzid, 'Pomona NY', "US", null, elevation);

export const ZmanEnum = {
    ALOS: 'ALOS',
    FAST_START: 'FAST_START',
    FAST_END: 'FAST_END',
    NETZ: 'NETZ',
    SHEMA: 'SHEMA',
    CHATZOS: 'CHATZOS',
    CANDLE_LIGHTING: 'CANDLE_LIGHTING',
    SHKIAH: 'SHKIAH',
    TZEIS: 'TZEIS',
    TZEIS6: 'TZEIS6',
    TZEIS8_5: 'TZEIS8_5',
    EARLIEST_SHKIAH_SUN_THUR: 'EARLIEST_SHKIAH_SUN_THUR',
    LATEST_SHKIAH_SUN_THUR: 'LATEST_SHKIAH_SUN_THUR',
    MOLAD: 'MOLAD',
    SEVENTH_HOUR: 'SEVENTH_HOUR'
};

export const titleMapper = {
    [ZmanEnum.ALOS]: 'עלות השחר',
    [ZmanEnum.FAST_START]: 'התחלת הצום',
    [ZmanEnum.FAST_END]: 'סיום הצום',
    [ZmanEnum.NETZ]: 'נץ החמה',
    [ZmanEnum.SHEMA]: 'סוף זמן ק"ש',
    [ZmanEnum.CHATZOS]: 'חצות',
    [ZmanEnum.CANDLE_LIGHTING]: 'הדלקת נרות',
    [ZmanEnum.SHKIAH]: 'שקיעה',
    [ZmanEnum.TZEIS]: 'צאת הכוכבים',
    [ZmanEnum.TZEIS6]: '(6) צאת הכוכבים',
    [ZmanEnum.TZEIS8_5]: '(8.5) צאת הכוכבים',
    [ZmanEnum.EARLIEST_SHKIAH_SUN_THUR]: 'שקיעה מוקדמת (א-ה)',
    [ZmanEnum.LATEST_SHKIAH_SUN_THUR]: 'שקיעה מאוחרת (א-ה)',
    [ZmanEnum.MOLAD]: 'מולד',
    [ZmanEnum.SEVENTH_HOUR]: 'איו לקדש'
};

export const WeeklyZmanimEnum = {
    FRIDAY_NIGHT: 'FRIDAY_NIGHT',
    SHABBOS_DAY: 'SHABBOS_DAY',
    SHABBOS_AFTERNOON: 'SHABBOS_AFTERNOON',
    SUNDAY: 'SUNDAY',
    WEEKDAYS: 'WEEKDAYS',
    FRIDAY_MORNING: 'FRIDAY_MORNING'
}

export function getDailyZmanim(date) {
    // Validate the input date
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return {
            zmanim: [],
            isCandleLightingToday: false,
            isMotzeiShabbos: false,
            motzeiInfo: null,
            getZman: () => null,
            isShabbosMevorchim: false,
            omerInfo: null
        };
    }

    const hdate = new HDate(date);
    const zm = new Zmanim(gloc, date, false);

    // Get comprehensive motzei information
    const motzeiInfo = getMotzeiInfo(date, location);

    // Basic core times
    const alos16 = zm.getTemporalHourByDeg(16.9);
    const alos = alos16 ? roundUpToMinute(alos16[0]) : null;
    const fastStart = alos16 ? roundDownToMinute(alos16[0]) : null;
    const netz = roundUpToMinute(zm.sunrise());
    const shkiah = roundDownToMinute(zm.shkiah(true));
    
    // Use getTzeisTimes to determine the appropriate tzeis time
    const tzeisTimes = getTzeisTimes(date, location);
    const tzeis = tzeisTimes.tzeisTime;
    
    const chatzos = roundDownToMinute(zm.chatzot());
    const sofZmanShema = roundDownToMinute(getProportionalHoursAfterHanetz(gloc, date, 3));

    // Candle lighting (Erev Shabbos / Yom Tov)
    const candleLightingRaw = zm.sunsetOffset(-18, true);
    const isErevShabbosOrYT = isCandleLightingToday(date, location);
    const candleLighting = isErevShabbosOrYT && candleLightingRaw ? roundDownToMinute(candleLightingRaw) : null;
    // Motzei Shabbos (Havdalah day => 8.5 deg)
    const motzeiShabbosTime = tzeis;

    // Build the array
    const zmanimEntries = [];

    zmanimEntries.push({ zman: ZmanEnum.ALOS, title: titleMapper[ZmanEnum.ALOS], time: alos, });
    zmanimEntries.push({ zman: ZmanEnum.NETZ, title: titleMapper[ZmanEnum.NETZ], time: netz, });
    zmanimEntries.push({ zman: ZmanEnum.SHEMA, title: titleMapper[ZmanEnum.SHEMA], time: sofZmanShema, });
    zmanimEntries.push({ zman: ZmanEnum.CHATZOS, title: titleMapper[ZmanEnum.CHATZOS], time: chatzos, });
    if (candleLighting) {
        zmanimEntries.push({ zman: ZmanEnum.CANDLE_LIGHTING, title: titleMapper[ZmanEnum.CANDLE_LIGHTING], time: candleLighting, });
    } 
    zmanimEntries.push({ zman: ZmanEnum.SHKIAH, title: titleMapper[ZmanEnum.SHKIAH], time: shkiah, });
    
    // Use motzeiInfo to determine which tzeis to display

    zmanimEntries.push({ zman: ZmanEnum.TZEIS, title: titleMapper[ZmanEnum.TZEIS], time: tzeis, });

    // Add TZEIS6 and TZEIS8_5 (may be null if not available)
    if (tzeisTimes.tzeis6) {
        zmanimEntries.push({ zman: ZmanEnum.TZEIS6, title: titleMapper[ZmanEnum.TZEIS6], time: tzeisTimes.tzeis6, });
    }
    if (tzeisTimes.tzeis85) {
        zmanimEntries.push({ zman: ZmanEnum.TZEIS8_5, title: titleMapper[ZmanEnum.TZEIS8_5], time: tzeisTimes.tzeis85, });
    }

    // Add weekday-specific zmanim (Sunday-Thursday)
    const dayOfWeek = date.getDay();
    if (dayOfWeek >= 0 && dayOfWeek <= 4) { // Sunday to Thursday
        // Calculate earliest and latest shkiah for the week
        const weekDates = getWeekDates(date);
        let earliestShkiah = null;
        let latestShkiah = null;
        // Helper to get time-of-day in milliseconds (ignoring the date portion)
        const getTimeOfDay = (date) => {
            return date.getHours() * 3600000 + date.getMinutes() * 60000 + date.getSeconds() * 1000 + date.getMilliseconds();
        };
        for (let d of weekDates) {
            const zm = new Zmanim(gloc, d, false);
            const shkiah = zm.shkiah();

            if (!earliestShkiah || getTimeOfDay(shkiah) < getTimeOfDay(earliestShkiah)) {
                earliestShkiah = shkiah;
            }
            if (!latestShkiah || getTimeOfDay(shkiah) > getTimeOfDay(latestShkiah)) {
                latestShkiah = shkiah;
            }
        }

        const earliestShkiahSunThur = earliestShkiah ? roundDownToMinute(earliestShkiah) : null;
        const latestShkiahSunThur = latestShkiah ? roundUpToMinute(latestShkiah) : null;

        if (earliestShkiahSunThur) {
            zmanimEntries.push({ zman: ZmanEnum.EARLIEST_SHKIAH_SUN_THUR, title: titleMapper[ZmanEnum.EARLIEST_SHKIAH_SUN_THUR], time: earliestShkiahSunThur });
        }
        if (latestShkiahSunThur) {
            zmanimEntries.push({ zman: ZmanEnum.LATEST_SHKIAH_SUN_THUR, title: titleMapper[ZmanEnum.LATEST_SHKIAH_SUN_THUR], time: latestShkiahSunThur });
        }
    }

    let isShabbosMevorchim = checkShabbosMevorchim(date);
    if (isShabbosMevorchim && hdate.getMonth() !== 7) {

        let moladMonth = hdate.mm + 1; // mm is 1-indexed (e.g. Adar is 12 or 13 in leap years)
        let moladYear = hdate.yy;
        const maxMoladMonth = hdate.isLeapYear() ? 13 : 12;

        if (moladMonth > maxMoladMonth) {
            moladMonth = 1;
            moladYear = hdate.yy + 1; // Increment year when going to next year
        }
        
        // Special case: If we're in Elul (month 6) and next month is Tishrei (month 7), increment year
        if (hdate.mm === 6) {
            moladYear = hdate.yy + 1;
        }

        // console.log('Molad calculation - hdate.mm:', hdate.mm, 'moladMonth:', moladMonth, 'moladYear:', moladYear, 'hdate.yy:', hdate.yy);
        const moladEvent = new MoladEvent(hdate, moladYear, moladMonth);

        const molad = moladEvent.render('he');
        //console.log(molad, moladEvent);
        //let moladValue = `יּוֹם ${moladEvent.molad.getDow()},${moladEvent.molad.getHour()} בַּבֹּקֶר, ${moladEvent.molad.getMinutes()} דַּקּוֹת ${moladEvent.molad.getChalakim()} חֲלָקִים`;
        const rawHour = moladEvent.molad.getHour();
        const rawMinutes = moladEvent.molad.getMinutes();
        const rawChalakim = moladEvent.molad.getChalakim();
        const displayHour = (rawHour === 0) ? 12 : (rawHour > 12 ? rawHour - 12 : rawHour);
        
        // Determine period text based on time and shkiah
        let periodText = "בבוקר"; // default to morning
        if (rawHour >= 12) {
            // Need to check if molad is before or after shkiah
            // Find the exact day of the molad by matching the day of week
            const moladDow = moladEvent.molad.getDow(); // 0 = Sunday, 6 = Saturday
            let moladDay = 15; // Start with middle of month
            let moladHDate = new HDate(moladDay, moladMonth, moladYear);
            let foundDay = false;
            
            // Search around day 15 to find the day that matches the day of week
            for (let offset = 0; offset <= 7 && !foundDay; offset++) {
                const testDay1 = 15 + offset;
                const testDay2 = 15 - offset;
                
                // Try day 15 + offset
                if (testDay1 <= 30) {
                    try {
                        const testHDate = new HDate(testDay1, moladMonth, moladYear);
                        if (testHDate.getDay() === moladDow) {
                            moladDay = testDay1;
                            moladHDate = testHDate;
                            foundDay = true;
                            break;
                        }
                    } catch (e) {
                        // Invalid day, skip
                    }
                }
                
                // Try day 15 - offset (if not already found and offset > 0)
                if (!foundDay && offset > 0 && testDay2 >= 1) {
                    try {
                        const testHDate = new HDate(testDay2, moladMonth, moladYear);
                        if (testHDate.getDay() === moladDow) {
                            moladDay = testDay2;
                            moladHDate = testHDate;
                            foundDay = true;
                            break;
                        }
                    } catch (e) {
                        // Invalid day, skip
                    }
                }
            }
            
            const moladGregDate = moladHDate.greg();
            
            // Calculate shkiah for the molad date
            const moladZm = new Zmanim(gloc, moladGregDate, false);
            const moladShkiah = roundDownToMinute(moladZm.shkiah(true));
            
            // Create Date object for molad time
            // Convert chalakim to minutes (1 chelek = 1/18 minute)
            const chalakimInMinutes = rawChalakim / 18;
            const moladTime = new Date(moladGregDate);
            moladTime.setHours(rawHour, rawMinutes + Math.floor(chalakimInMinutes), Math.round((chalakimInMinutes % 1) * 60), 0);
            
            // Compare molad time with shkiah
            if (moladTime < moladShkiah) {
                periodText = "אהצ\"ם"; // אחר הצהריים (afternoon)
            } else {
                periodText = "בערב"; // evening
            }
        }
        
        // Map day of week to Hebrew letter
        const hebrewDayLetters = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז'];
        const dayIndex = moladEvent.molad.getDow(); // 0-based index (0 = Sunday)
        const hebrewDayLetter = hebrewDayLetters[dayIndex];
        
        const moladValue = `'יום ${hebrewDayLetter} ${periodText}, שעה ${displayHour}, ${moladEvent.molad.getMinutes()} דק' ו${moladEvent.molad.getChalakim()} חל`;


        // console.log(moladValue);
        zmanimEntries.push({ zman: ZmanEnum.MOLAD, title: titleMapper[ZmanEnum.MOLAD], value: moladValue });
        
        // Only add FAST_START on Shabbos Mevorchim if it's actually a fast day or day before major fast
        const fastInfo = getFastInfo(date, gloc);
        const shouldShowFastStart = (fastInfo.isTodayFast || fastInfo.isTomorrowMajorFast) && 
                                    (fastInfo.todayFastStartTime || fastInfo.tomorrowFastStartTime);
        if (shouldShowFastStart) {
            zmanimEntries.push({ zman: ZmanEnum.FAST_START, title: titleMapper[ZmanEnum.FAST_START], time: fastInfo.todayFastStartTime || fastInfo.tomorrowFastStartTime, });
        }

        //:` :} ${moladEvent.molad.getChalakim()} Chalokim` });
    }

    const result = {
        zmanim: zmanimEntries,
        isCandleLightingToday: candleLighting,
        isMotzeiShabbos: motzeiInfo.isMotzeiShabbos,
        motzeiInfo: motzeiInfo,
        getZman: (zEnum) => {
            return zmanimEntries.find((z) => z.zman === zEnum)?.time || null;
        },
        isShabbosMevorchim: isShabbosMevorchim,
        // Add omer info if in the omer counting period
        omerInfo: getOmerInfo(date, motzeiInfo.isAfterTzeis)
    };

    return result;
}

/**
 * Gets the Omer information for the given date
 * @param {Date} date - The date to check
 * @param {boolean} isAfterTzeis - Whether the current time is after Tzeis
 * @returns {Object|null} - Omer information or null if not in the Omer period
 */
function getOmerInfo(date, isAfterTzeis) {
    // If after tzeis, get tomorrow's omer info
    const targetDate = isAfterTzeis ? new Date(date.getTime() + 24*60*60*1000) : date;
    const omerDay = getOmerDay(targetDate);
    
    // If there is no omer on the current date, return null
    if (!omerDay) return null;
    
    const sefirahInfo = getSefirahInfo(targetDate);
    return {
        day: omerDay,
        sefirah: sefirahInfo.sefirah,
        attribute: sefirahInfo.attribute,
        display: [
            `היום ${sefirahInfo.letter} ימים לעומר`,
            `${sefirahInfo.attribute}`,
            `Day ${sefirahInfo.day} of the Omer`
        ]
    };
}

function getFridayNightZmanim(shabbosDate) {
    // The day before Shabbos is Friday:
    const friday = new Date(shabbosDate);
    friday.setDate(friday.getDate() - 1);

    const baseDaily = getDailyZmanim(friday);
    const shkiah = baseDaily.getZman(ZmanEnum.SHKIAH);

    const mincha = new Date(shkiah.getTime() - 10 * 60 * 1000);
    const kabbolas = new Date(shkiah.getTime() + 22 * 60 * 1000);
    /*
    let zmanimEntries = [
        { zman: ZmanEnum.EREV_SHABBOS_MINCHA, title: titleMapper[ZmanEnum.EREV_SHABBOS_MINCHA], time: roundDownToMinute(mincha) },
        { zman: ZmanEnum.SHKIAH, title: titleMapper[ZmanEnum.SHKIAH], time: roundDownToMinute(shkiah) },
        { zman: ZmanEnum.EREV_SHABBOS_KABOLAS_SHABBOS, title: titleMapper[ZmanEnum.EREV_SHABBOS_KABOLAS_SHABBOS], time: roundDownToMinute(kabbolas) },
    ]
    
    return {
        zmanim: zmanimEntries,
        getZman: (zEnum) => {
            return zmanimEntries.find((z) => z.zman === zEnum)?.time || null;
        }
    };*/

    const isDST = friday.getTimezoneOffset() < Math.max(
        new Date(friday.getFullYear(), 0, 1).getTimezoneOffset(),
        new Date(friday.getFullYear(), 6, 1).getTimezoneOffset()
    );
    const seventhHourValue = isDST ? '6:56 - 7:57 pm' : '5:56 - 6:57 pm';

    
    // Extract the end time from seventhHourValue for comparison
    const seventhHourEndTime = isDST ? '7:57 pm' : '6:57 pm';
    const kabolasTime = roundDownToMinute(kabbolas);
    
    // Only push the seventh hour zman if kabbolas time is not after the end time of the seventh hour
    const kabolasHours = kabolasTime.getHours();
    const kabolasMinutes = kabolasTime.getMinutes();
    const seventhHourEndHours = parseInt(seventhHourEndTime.split(':')[0]);
    const seventhHourEndMinutes = parseInt(seventhHourEndTime.split(':')[1].split(' ')[0]);
    
    // Convert to 24-hour format for comparison if needed
    const kabolasIn24Format = (kabolasHours % 12) + (kabolasTime.getHours() >= 12 ? 12 : 0);
    const seventhHourEndIn24Format = seventhHourEndHours + (seventhHourEndTime.includes('pm') ? 12 : 0);
    
    // Compare times
    if (kabolasIn24Format < seventhHourEndIn24Format || 
        (kabolasIn24Format === seventhHourEndIn24Format && kabolasMinutes <= seventhHourEndMinutes)) {
        baseDaily.zmanim.push({ zman: ZmanEnum.SEVENTH_HOUR, title: titleMapper[ZmanEnum.SEVENTH_HOUR], value: seventhHourValue });
    }

    return baseDaily;
}

function getShabbosDayZmanim(shabbosDate) {
    const baseDaily = getDailyZmanim(shabbosDate);

    baseDaily.zmanim.push(baseDaily.getZman(ZmanEnum.SHEMA));
    // Sort by time
    return baseDaily;
}

function getSundayThroughThursdayZmanim(shabbosDate) {
    const results = [];

    // If 'shabbosDate' is e.g. Saturday, then Sunday is +1 day, Monday is +2, etc.
    for (let i = 1; i <= 5; i++) {
        const dt = new Date(shabbosDate);
        dt.setDate(dt.getDate() + i); // Sunday=+1, Monday=+2, ...
        dt.setHours(12, 0, 0, 0);
        const dayZmanim = getDailyZmanim(dt);
        results.push({ date: dt, zmanim: dayZmanim });
    }

    // Resolve the week's generic zmanim from Sunday, after any overnight clock
    // change. Using Shabbos here left SHKIAH/TZEIS-based weekday services an hour
    // off on DST transition weeks. Sunday's data also includes Sun-Thu extrema.
    return { ...results[0].zmanim, allWeekZmanim: results };
}

function getNextFridayMorningZmanim(shabbosDate) {

    const nextFriday = new Date(shabbosDate);
    nextFriday.setDate(nextFriday.getDate() + 6);

    const allFridayZmanim = getDailyZmanim(nextFriday);

    //const wantedZmanim = ['Alos', 'Netz', 'Chatzos', 'Shema'];

    /*const nextFridayZmanim = allFridayZmanim.zmanim.filter(w =>
        wantedZmanim.includes(w.zman)
    );*/

    return allFridayZmanim;
}

//This method will get the times for friday night, shabbos and the following week. 
//This means that until Thursday night we should be using this method with last weeks shabbos date.
//From Thursday night it should be the next shabbbos
export function weeklyZmanim(date) {

    let shabbosDate = getAdjustedShabbos(date || new Date());

    const fridayNight = getFridayNightZmanim(shabbosDate);
    const shabbosDayZmanim = getShabbosDayZmanim(shabbosDate);
    const sunToThu = getSundayThroughThursdayZmanim(shabbosDate);
    const nextFriday = getNextFridayMorningZmanim(shabbosDate);

    let nextFridayDate = new Date(shabbosDate);
    nextFridayDate.setDate(shabbosDate.getDate() + 6);

    let weeklys =
    {
        [WeeklyZmanimEnum.FRIDAY_NIGHT]: fridayNight,
        [WeeklyZmanimEnum.SHABBOS_DAY]: shabbosDayZmanim,
        [WeeklyZmanimEnum.WEEKDAYS]: sunToThu,
        //[WeeklyZmanimEnum.WEEKDAYS]: nextFriday,
        isShabbosMevorchim: () => checkShabbosMevorchim(shabbosDate),
        dateRange: { start: shabbosDate, end: nextFridayDate }
    };

    return weeklys;
}


export function getProportionalHoursAfterHanetz(gloc, date, hours) {
    try {
        const zmanim = new Zmanim(gloc, date, true);
        const hanetzAmiti = zmanim.timeAtAngle(1.583, true);
        const shkiah = zmanim.sunset();
        if (!hanetzAmiti || !shkiah) return null;
        const temporalHour = (shkiah.getTime() - hanetzAmiti.getTime()) / 12;
        return new Date(hanetzAmiti.getTime() + (hours * temporalHour));
    } catch (error) {
        // Return null for any calculation errors (invalid timezone, etc.)
        return null;
    }
}

export function getAdjustedChatzotForDecember25(gloc) {
    const todayHebrew = new HDate();
    const jewishYear = todayHebrew.getFullYear();

    let dec25 = new Date(new Date().getFullYear(), 11, 25);
    let dec25Hebrew = new HDate(dec25);

    if (dec25Hebrew.getFullYear() !== jewishYear) {
        dec25 = new Date(new Date().getFullYear() - 1, 11, 25);
        dec25Hebrew = new HDate(dec25);
    }

    const zmanim = new Zmanim(gloc, dec25, false);
    const chatzotTime = zmanim.chatzot();

    //console.log("Chatzot Time:", chatzotTime.toLocaleTimeString());

    let adjustedTime = new Date(chatzotTime);
    adjustedTime.setHours(adjustedTime.getHours() + 6);

    //console.log("Adjusted Time (+6 hrs):", adjustedTime.toLocaleTimeString());

    let startTime = roundDownToMinute(adjustedTime);
    //console.log("Start Time (Rounded Down):", startTime.toLocaleTimeString());

    let endTime = roundUpToMinute(new Date(startTime.getTime() + 60 * 60 * 1000));
    //console.log("End Time (Rounded Up):", endTime.toLocaleTimeString());

    return {
        date: dec25.toLocaleDateString(),
        hebrewDate: dec25Hebrew.toString(),
        chatzot: chatzotTime.toLocaleTimeString(),
        adjustedTime: adjustedTime.toLocaleTimeString(),
        startTime: startTime.toLocaleTimeString(),
        endTime: endTime.toLocaleTimeString(),
    };
}

function roundUpToMinute(date) {
    let newDate = new Date(date);
    if (newDate.getSeconds() > 0 || newDate.getMilliseconds() > 0) {
        newDate.setMinutes(newDate.getMinutes() + 1);
    }
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
}

function roundDownToMinute(date) {
    let newDate = new Date(date);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
}

function calculateSeventhHour(date) {
    // Calculate SEVENTH_HOUR based on DST and time comparisons
    // This replicates the logic from getDailyZmanim function

    // Check if it's Friday (day 5) - only applicable on Friday
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 5) {
        return null; // Not Friday, no seventh hour
    }

    const isDST = date.getTimezoneOffset() < Math.max(
        new Date(date.getFullYear(), 0, 1).getTimezoneOffset(),
        new Date(date.getFullYear(), 6, 1).getTimezoneOffset()
    );

    const seventhHourValue = isDST ? '6:56 - 7:57 pm' : '5:56 - 6:57 pm';
    const seventhHourEndTime = isDST ? '7:57 pm' : '6:57 pm';

    // Get Kabolas Shabbos time
    const fridayDate = getAdjustedFriday(date);
    const fridayZmanim = new Zmanim(gloc, fridayDate, false);
    const fridayShkiah = fridayZmanim.shkiah();
    const kabolasTime = roundDownToMinute(new Date(fridayShkiah.getTime() + 22 * 60 * 1000));

    // Parse times for comparison
    const kabolasHours = kabolasTime.getHours();
    const kabolasMinutes = kabolasTime.getMinutes();
    const seventhHourEndHours = parseInt(seventhHourEndTime.split(':')[0]);
    const seventhHourEndMinutes = parseInt(seventhHourEndTime.split(':')[1].split(' ')[0]);

    // Convert to 24-hour format for comparison
    const kabolasIn24Format = (kabolasHours % 12) + (kabolasTime.getHours() >= 12 ? 12 : 0);
    const seventhHourEndIn24Format = seventhHourEndHours + (seventhHourEndTime.includes('pm') ? 12 : 0);

    // Compare times
    if (kabolasIn24Format < seventhHourEndIn24Format ||
        (kabolasIn24Format === seventhHourEndIn24Format && kabolasMinutes <= seventhHourEndMinutes)) {
        return seventhHourValue;
    }

    return null; // Seventh hour doesn't apply
}


export function zmanLoader(date) {
    // Use getDailyZmanim as the base to avoid duplication
    const dailyZmanim = getDailyZmanim(date);
    const hdate = new HDate(date);
    
    // Convert entries from { zman, title, time } to { title, zman, time } format
    // and ensure all entries are included
    const allEntries = dailyZmanim.zmanim.map(entry => ({
        title: entry.title,
        zman: entry.zman,
        time: entry.time,
        value: entry.value
    }));

    // Earliest/latest shkiah are only needed during the week (Sunday-Thursday)
    // They're already included in getDailyZmanim for those days, so no need to add them here

    // Only include FAST_START and FAST_END if they should be displayed:
    // - FAST_START: if today is a fast day OR tomorrow is a major fast (and time is not null)
    // - FAST_END: if today is a fast day (and time is not null)
    const fastInfo = getFastInfo(date, gloc);
    const existingFastStart = allEntries.find(e => e.zman === ZmanEnum.FAST_START);
    const existingFastEnd = allEntries.find(e => e.zman === ZmanEnum.FAST_END);
    
    // Determine if FAST_START should be shown
    const shouldShowFastStart = (fastInfo.isTodayFast || fastInfo.isTomorrowMajorFast) && 
                                (fastInfo.todayFastStartTime || fastInfo.tomorrowFastStartTime);
    
    // Determine if FAST_END should be shown
    const shouldShowFastEnd = fastInfo.isTodayFast && fastInfo.todayFastEndTime;
    
    if (!existingFastStart && shouldShowFastStart) {
        allEntries.push({
            title: titleMapper[ZmanEnum.FAST_START],
            zman: ZmanEnum.FAST_START,
            time: fastInfo.todayFastStartTime || fastInfo.tomorrowFastStartTime
        });
    }
    if (!existingFastEnd && shouldShowFastEnd) {
        allEntries.push({
            title: titleMapper[ZmanEnum.FAST_END],
            zman: ZmanEnum.FAST_END,
            time: fastInfo.todayFastEndTime
        });
    }

    // Add SEVENTH_HOUR if applicable (not in getDailyZmanim)
    const seventhHour = calculateSeventhHour(date);
    if (seventhHour && !allEntries.find(e => e.zman === ZmanEnum.SEVENTH_HOUR)) {
        allEntries.push({
            title: titleMapper[ZmanEnum.SEVENTH_HOUR],
            zman: ZmanEnum.SEVENTH_HOUR,
            value: seventhHour
        });
    }

    return {
        zmanim: allEntries,
        isShabbosMevorchim: () => checkShabbosMevorchim(date),
        motzeiInfo: dailyZmanim.motzeiInfo,
        getZman: (zman) => {
            const found = allEntries.find(z => z.zman === zman);
            return found ? (found.time || found.value) : null;
        },
        // Add omer info if in the omer counting period
        omerInfo: dailyZmanim.omerInfo
    };
}

// Helper: If today is Saturday (day 6), use the previous Friday; otherwise use the upcoming Friday.
function getAdjustedFriday(date) {
    const d = new Date(date);
    const day = d.getDay();
    if (day === 6) {
        // Saturday – use yesterday (Friday)
        d.setDate(d.getDate() - 1);
    } else {
        // For Sunday–Friday, get the next Friday.
        let diff = (5 - day + 7) % 7;
        if (diff === 0) {
            // If today is Friday, jump a full week ahead.
            diff = 7;
        }
        d.setDate(d.getDate() + diff);
    }
    return d;
}

// Helper: For Shabbos, if today is Saturday use current date; otherwise, get the upcoming Saturday.
function getAdjustedShabbos(date) {
    const d = new Date(date);
    const day = d.getDay();
    if (day === 6) {
        return d;
    } else {
        let diff = (6 - day + 7) % 7;
        if (diff === 0) {
            diff = 7;
        }
        d.setDate(d.getDate() + diff);
        return d;
    }
}

// Helper: Returns an array of dates representing Sunday–Thursday for the "weekday" week.
// If today is Sun–Thu, we use this week; if it's Fri or Sat, we use the upcoming week.
function getWeekDates(date) {
    const d = new Date(date);
    // Normalize to noon to avoid DST issues
    d.setHours(12, 0, 0, 0);
    const day = d.getDay();
    let sunday;
    if (day >= 0 && day <= 4) {
        // Current week: get this week's Sunday
        sunday = new Date(d);
        sunday.setDate(d.getDate() - day);
    } else {
        // If Friday (5) or Saturday (6), use the upcoming Sunday.
        const diff = 7 - day;
        sunday = new Date(d);
        sunday.setDate(d.getDate() + diff);
    }
    // Build an array for Sunday through Thursday.
    const dates = [];
    for (let i = 0; i < 5; i++) {
        const newDate = new Date(sunday);
        newDate.setDate(sunday.getDate() + i);
        // Ensure all dates are normalized to noon
        newDate.setHours(12, 0, 0, 0);
        dates.push(newDate);
    }
    return dates;
}



function checkShabbosMevorchim(date) {
    const hdate = new HDate(date);
    if (hdate.getDay() !== 6) {
        return false;
    }

    // Special case: If we're in Elul and the next month is Tishrei (Rosh Hashanah),
    // consider it Shabbos Mevorchim even though traditionally it's not
    if (hdate.getMonth() === 12) { // Elul is month 12
        for (let i = 1; i <= 7; i++) {
            let upcomingDate = hdate.add(i);
            if (upcomingDate.getMonth() === 1) { // Tishrei is month 1
                return true;
            }
        }
    }

    const todayEvents = HebrewCalendar.calendar({ start: hdate, end: hdate });
    // If Rosh Chodesh or Rosh Hashana falls on this Shabbos, it's not mevorchim
    if (todayEvents.some(event => event.desc.includes("Rosh Chodesh") || event.desc.includes("Rosh Hashana"))) {
        return false;
    }

    for (let i = 1; i <= 7; i++) {
        let upcomingDate = hdate.add(i);
        let events = HebrewCalendar.calendar({ start: upcomingDate, end: upcomingDate });
        if (events.some(event => event.desc.includes("Rosh Chodesh") || event.desc.includes("Rosh Hashana"))) {
            return true;
        }
    }
    return false;
}

function isCandleLightingToday(date, _location) {
    const today = new HDate(date);

    // First, try with candlelighting: true to get events with LIGHT_CANDLES flag
    const candleEvents = HebrewCalendar.calendar({ start: today, end: today, candlelighting: true, location: _location });

    // Check for LIGHT_CANDLES flag
    if (candleEvents.some(event => (event.mask & flags.LIGHT_CANDLES) !== 0)) {
        return true;
    }

    // Check if today is Yom Kippur or tomorrow is Yom Kippur
    // Get today's events (already have candleEvents)
    const todayIsYomKippur = candleEvents.some(event =>
        event.desc && (event.desc.toLowerCase().includes('yom kippur') || event.desc.toLowerCase().includes('yom-kippur'))
    );

    // Get tomorrow's events to see if tomorrow is Yom Kippur
    const tomorrowHDate = today.add(1);
    const tomorrowEvents = HebrewCalendar.calendar({ start: tomorrowHDate, end: tomorrowHDate, location: _location });

    const tomorrowIsYomKippur = tomorrowEvents.some(event =>
        event.desc && (event.desc.toLowerCase().includes('yom kippur') || event.desc.toLowerCase().includes('yom-kippur'))
    );


    return todayIsYomKippur || tomorrowIsYomKippur;
}

/**
 * Determines if a given date is a potential motzei day (Shabbos or Yom Tov)
 */
function isPotentialMotzeiDay(date, location) {
    const hdate = new HDate(date);
    
    // Check if today is Saturday (potential motzei Shabbos)
    const isShabbos = hdate.getDay() === 6;
    
    // Check if today has YOM_TOV_ENDS flag (potential motzei Yom Tov)
    const events = HebrewCalendar.calendar({ 
        start: hdate, 
        end: hdate, 
        location: location 
    });
    
    const hasYomTovEndsFlag = events.some(event => 
        (event.mask & flags.YOM_TOV_ENDS) !== 0);
    
    // Check for Havdalah events
    const hasHavdalahEvent = events.some(event => 
        event.desc.includes("Havdalah") || 
        event.desc.includes("הבדלה") ||
        (event.mask & flags.HAVDALAH) !== 0);
    
    return isShabbos || hasYomTovEndsFlag || hasHavdalahEvent;
}

/**
 * Determines which tzeis calculation to use based on the date and special day types
 * @param {Date} date - The date to check
 * @param {Location} location - The location object
 * @returns {Object} Object containing tzeis times and which one should be used
 */
export function getTzeisTimes(date, location) {
    const zm = new Zmanim(gloc, date, false);
    const tzeis6 = roundUpToMinute(zm.tzeit(6));
    const tzeis85 = roundUpToMinute(zm.tzeit(8.5));
    
    const hdate = new HDate(date);
    const isShabbos = hdate.getDay() === 6;
    
    // Get events for today
    const events = HebrewCalendar.calendar({ 
        start: hdate, 
        end: hdate, 
        location: location 
    });
        
    // Check if today has YOM_TOV_ENDS flag (potential motzei Yom Tov)
    const hasYomTovEndsFlag = events.some(event => 
        (event.mask & flags.YOM_TOV_ENDS) !== 0);
    
    // Use getSpecialDayForDate to determine special days
    const specialDay = getSpecialDayForDate(date);
    const isYomKippur = specialDay?.specialDayType === SpecialDayTypeEnum.YOM_KIPPUR;
    
    // Check for Yom Tov
    const isPesach = specialDay?.specialDayType === SpecialDayTypeEnum.PESACH;
    const isSukkot = specialDay?.specialDayType === SpecialDayTypeEnum.SUKKOT;
    const isShavuot = specialDay?.specialDayType === SpecialDayTypeEnum.SHAVUOT;
    const isRoshHashana = specialDay?.specialDayType === SpecialDayTypeEnum.ROSH_HASHANAH;
    
    const isYomTov = isPesach || isSukkot || isShavuot || isRoshHashana ||
                    events.some(event => (event.mask & flags.YOM_TOV) !== 0);
    
    // Check for the day before and after
    const yesterday = new Date(date);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const tomorrowSpecialDay = getSpecialDayForDate(tomorrow);
    const isTomorrowYomTov = tomorrowSpecialDay?.specialDayType === SpecialDayTypeEnum.PESACH ||
                            tomorrowSpecialDay?.specialDayType === SpecialDayTypeEnum.SUKKOT ||
                            tomorrowSpecialDay?.specialDayType === SpecialDayTypeEnum.SHAVUOT ||
                            tomorrowSpecialDay?.specialDayType === SpecialDayTypeEnum.ROSH_HASHANAH ||
                            HebrewCalendar.calendar({
                                start: new HDate(tomorrow),
                                end: new HDate(tomorrow),
                                location: location
                            }).some(event => (event.mask & flags.YOM_TOV) !== 0);
    
    // Two-day Yom Tov check (today is first day)
    const isTwoDayYomTov = isYomTov && isTomorrowYomTov;
    
    // Determine if we need to use tzeis 8.5
    const useTzeis85 = 
        // Motzei Shabbos scenario
        isShabbos || 
        // Motzei Yom Tov scenario
        hasYomTovEndsFlag || 
        // Days between Shabbos and Yom Tov
        (isShabbos && isTomorrowYomTov) || // Shabbos into Yom Tov 
        (isYomTov && tomorrow.getDay() === 6) || // Yom Tov into Shabbos
        // Two-day Yom Tov
        isTwoDayYomTov ||
        // Yom Kippur
        isYomKippur
    
    // Return all relevant tzeis times and which one should be used
    return {
        tzeis6: tzeis6,
        tzeis85: tzeis85,
        useTzeis85: useTzeis85,
        // Return the appropriate tzeis time based on the determination
        tzeisTime: useTzeis85 ? tzeis85 : tzeis6
    };
}

/**
 * Determines comprehensive motzei status for a given date
 * @param {Date} date - The date to check
 * @param {Location} location - The location object
 * @returns {Object} Detailed motzei information
 */
function getMotzeiInfo(date, location) {
    // Step 1: Determine if today is a potential motzei day
    const isPotentialMotzei = isPotentialMotzeiDay(date, location);
    
    // Step 2: Get the appropriate tzeis time using the new function
    const tzeisTimes = getTzeisTimes(date, location);
    const tzeisTime = tzeisTimes.tzeisTime;
    
    // Step 3: Check if current time is after tzeis
    const now = new Date();
    const isAfterTzeisTime = now.getTime() > tzeisTime.getTime();
    
    // Basic motzei determinations
    const hdate = new HDate(date);
    const isShabbos = hdate.getDay() === 6;
    const events = HebrewCalendar.calendar({ 
        start: hdate, 
        end: hdate, 
        location: location 
    });
    
    const hasYomTovEndsFlag = events.some(event => 
        (event.mask & flags.YOM_TOV_ENDS) !== 0);
    
    // Check if tomorrow is Yom Tov
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const tomorrowEvents = HebrewCalendar.calendar({
        start: new HDate(tomorrow),
        end: new HDate(tomorrow),
        location: location
    });
    
    const isTomorrowYomTov = tomorrowEvents.some(event => 
        (event.mask & flags.YOM_TOV) !== 0);
    
    // Determine specific motzei types
    const isMotzeiShabbos = isShabbos && isAfterTzeisTime;
    const isMotzeiYomTov = hasYomTovEndsFlag && isAfterTzeisTime;
    
    // Determine if holiness continues (Shabbos into Yom Tov)
    const holinessContinues = isMotzeiShabbos && isTomorrowYomTov;
    
    // Only consider it an actual motzei if it's a potential motzei day and after tzeis
    const isActualMotzei = isPotentialMotzei && isAfterTzeisTime;
    
    return {
        isMotzei: isActualMotzei,
        isMotzeiShabbos: isMotzeiShabbos,
        isMotzeiYomTov: isMotzeiYomTov,
        holinessContinues: holinessContinues,
        needsYaknehaz: holinessContinues,
        tzeisTime: tzeisTime,
        isAfterTzeis: isAfterTzeisTime,
        yaknehazText: holinessContinues ? "יקנה\"ז" : null,
        
        // For debugging/display
        motzeiType: holinessContinues ? "Shabbos into Yom Tov" :
                    isMotzeiShabbos ? "Shabbos" :
                    isMotzeiYomTov ? "Yom Tov" : "None"
    };
}

function isMotzeiToday(date, _location) {
    const today = new HDate(date);
    const events = HebrewCalendar.calendar({ start: today, end: today, location: _location });
    return events.some(event => event.desc && (event.desc.includes("Havdalah") || event.desc.includes("הבדלה")));
}

function isFastStartToday(date, _location) {
    const today = new HDate(date);

    // First, try with candlelighting: true to get events with LIGHT_CANDLES flag
    const candleEvents = HebrewCalendar.calendar({ start: today, end: today, candlelighting: true, location: _location });

    // Check for LIGHT_CANDLES flag
    if (candleEvents.some(event => (event.mask & flags.LIGHT_CANDLES) !== 0)) {
        return true;
    }

    // Check if today is Yom Kippur or tomorrow is Yom Kippur
    const todayIsYomKippur = candleEvents.some(event =>
        event.desc && (event.desc.toLowerCase().includes('yom kippur') || event.desc.toLowerCase().includes('yom-kippur'))
    );

    // Get tomorrow's events to see if tomorrow is Yom Kippur
    const tomorrowHDate = today.add(1);
    const tomorrowEvents = HebrewCalendar.calendar({ start: tomorrowHDate, end: tomorrowHDate, location: _location });

    const tomorrowIsYomKippur = tomorrowEvents.some(event =>
        event.desc && (event.desc.toLowerCase().includes('yom kippur') || event.desc.toLowerCase().includes('yom-kippur'))
    );

    return todayIsYomKippur || tomorrowIsYomKippur;
}

function isFastEndToday(date, _location) {
    const today = new HDate(date);
    const events = HebrewCalendar.calendar({ start: today, end: today.add(1, 'd'), candlelighting: false, location: _location, noMinorFast: false, noHolidays: false });

    const fastEvents = events.filter(event => ((event.mask & flags.MINOR_FAST) !== 0) || (event.mask & flags.MAJOR_FAST) !== 0);
    //console.log('fastEvents', fastEvents);
    return fastEvents;
}


function getFastInfo(date, gloc) {
    ////////////////////mariv should be 25 mins after shkiah as it's a fast day => For MINOR FAST DAY

    const today = new HDate(date);
    const yesterday = new HDate(date).subtract(1);
    const tomorrow = new HDate(date).add(1);

    // Get fast days for today and tomorrow
    const holidaysToday = HebrewCalendar.calendar({ start: today, end: today, noMinorFast: false });
    const holidaysTomorrow = HebrewCalendar.calendar({ start: tomorrow, end: tomorrow, noMinorFast: false });

    // Get zmanim for today, yesterday, and tomorrow
    const zmanimToday = new Zmanim(gloc, date, false);
    const zmanimYesterday = new Zmanim(gloc, yesterday, false); // -1 day
    const zmanimTomorrow = new Zmanim(gloc, tomorrow, false); // +1 day

    let isTodayFast = false;
    let isTomorrowFast = false;
    let isTomorrowMajorFast = false;
    let todayFastStartTime = null;
    let todayFastEndTime = null;
    let tomorrowFastStartTime = null;

    // Check today's fast
    holidaysToday.forEach((holiday) => {
        // Check for Yom Kippur specifically (it might not have the standard fast flags)
        if (holiday.desc.includes('Yom Kippur')) {
            isTodayFast = true;
            todayFastEndTime = roundUpToMinute(zmanimToday.tzeit(8.5)); // Yom Kippur ends at Tzeis 8.5
            todayFastStartTime = roundDownToMinute(zmanimToday.shkiah(true)); // Yom Kippur starts at Shkiah
        } else if (holiday.getFlags() & (flags.MINOR_FAST)) {
            isTodayFast = true;
            todayFastEndTime = roundUpToMinute(zmanimToday.tzeit(6)); // Fast ends at Tzeis

            if (holiday.getFlags() & flags.MINOR_FAST) {
                todayFastStartTime = roundDownToMinute(zmanimToday.getTemporalHourByDeg(16.9)[0]);
            }
        } else if (holiday.getFlags() & (flags.MAJOR_FAST)) {
            isTodayFast = true;
            todayFastEndTime = roundUpToMinute(zmanimToday.tzeit(8.5)); // Fast ends at Tzeis
        }
    });

    // Check tomorrow's fast
    holidaysTomorrow.forEach((holiday) => {
        // Check for Yom Kippur specifically
        if (holiday.desc.includes('Yom Kippur')) {
            isTomorrowFast = true;
            isTomorrowMajorFast = true;
            tomorrowFastStartTime = roundDownToMinute(zmanimToday.shkiah(true)); // Yom Kippur starts at Shkiah today
        } else if (holiday.getFlags() & (flags.MINOR_FAST | flags.MAJOR_FAST)) {
            isTomorrowFast = true;

            if (holiday.getFlags() & flags.MINOR_FAST) {
                tomorrowFastStartTime = roundDownToMinute(zmanimToday.getTemporalHourByDeg(16.9)[0]); // Minor fasts start at Alos
            } else if (holiday.getFlags() & flags.MAJOR_FAST) {
                isTomorrowMajorFast = true;
                tomorrowFastStartTime = roundDownToMinute(zmanimToday.shkiah(true)); // Major fasts start at Shkiah today
            }
        }
    });

    return {
        isTodayFast,
        isTomorrowFast,
        isTomorrowMajorFast,
        todayFastStartTime,
        todayFastEndTime,
        tomorrowFastStartTime,
    };
}

